import Papa from 'papaparse';
import { createContext, Dispatch, SetStateAction, useCallback, useContext, useState } from 'react';

export interface CsvUploadContextType<T, S> {
  file: File | null | undefined;
  csvParseResults: Papa.ParseResult<string[]> | null | undefined;
  setFile: (file: File | null | undefined) => void;
  setCsvParseResults: (results: Papa.ParseResult<string[]> | null | undefined) => void;
  results: T[] | undefined | null;
  setResults: Dispatch<SetStateAction<T[] | undefined | null>>;
  validationState: S | undefined | null;
  setValidationState: Dispatch<SetStateAction<S | undefined | null>>;
  reset: () => void;
}

export const CsvUploadContext = createContext<CsvUploadContextType<any, any> | null>(null);

export const CsvUploadProvider = ({ children }: { children: React.ReactNode }) => {
  const [file, setFile] = useState<File | null | undefined>(null);
  const [csvParseResults, setCsvParseResults] = useState<
    Papa.ParseResult<string[]> | null | undefined
  >(null);
  const [results, setResults] = useState<unknown[] | undefined | null>(null);
  const [validationState, setValidationState] = useState<unknown>(null);

  const reset = useCallback(() => {
    setFile(null);
    setCsvParseResults(null);
    setResults(null);
    setValidationState(null);
  }, []);

  return (
    <CsvUploadContext.Provider
      value={{
        file,
        csvParseResults,
        setFile,
        setCsvParseResults,
        results,
        setResults,
        validationState,
        setValidationState,
        reset,
      }}
    >
      {children}
    </CsvUploadContext.Provider>
  );
};

export const useCsvUpload = <T, S>() => {
  const context = useContext(CsvUploadContext) as CsvUploadContextType<T, S> | undefined;
  if (!context) {
    throw new Error('useCsvUpload must be used within a CsvUploadProvider');
  }
  return context;
};
